import { useContext, useMemo, useState } from 'react';
import React from 'react';
import { AssetTransaction } from '../../../../types/asset_transaction/assetTransaction';
import { AssetTransactionRequest } from '../../../../requests/assetTransaction.request';
import { AssetTransactionContext } from '../../../../context/AssetTransactionContext';
import { Button, CancelButton } from '../../../components/Button';
import { ActionModal } from '../../../components/Modal';
import { t } from '../../../../types/translation/Translator';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { useRequest } from '../../../../requests/common/requestHook';
import { useToast } from '../../../../providers/ToastProvider';
import { ContactContext } from '../../../../context/ContactContext';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { UserContext } from '../../../../context/UserContext';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { CustomFieldEntityParentType, CustomFieldEntityType, CustomFieldType } from '../../../../types/custom_field';
import TextInput from '../../Common/TextInput';
import CustomFieldInput from '../../Common/CustomFieldInput';

interface OwnershipAssetModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  transaction: AssetTransaction;

  disabled?: boolean;
}

export default function OwnershipAssetModal({
  open,
  setOpen,
  transaction,
  disabled = false,
}: OwnershipAssetModalProps) {
  const { showToast } = useToast();

  const { assetTransactions, setAssetTransactions } = useContext(AssetTransactionContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { companyUsers } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);
  const { contacts } = useContext(ContactContext);

  const [input, setInput] = useState<AssetTransaction>(new AssetTransaction({ ...transaction }));

  const [confirm, { loading: confirmLoading }] = useRequest(AssetTransactionRequest.confirm);
  const [reject, { loading: rejectLoading }] = useRequest(AssetTransactionRequest.reject);

  const assetCustomFields = useMemo(() => {
    return [...customFields.values()].filter(cf => cf.entityType === CustomFieldEntityType.asset);
  }, [customFields]);

  const assetTransactionCustomFields = useMemo(() => {
    return [...customFields.values()].filter(
      cf =>
        cf.entityType === CustomFieldEntityType.assetTransaction &&
        cf.entityParentType === CustomFieldEntityParentType.ownership,
    );
  }, [customFields]);

  const handleConfirm = async () => {
    const item = new AssetTransaction({ ...input });
    (item as any).customFields = item.customFieldValues();

    const result = await confirm({ data: [item] });
    if (!result) return;

    const transaction = result[0];
    assetTransactions.set(transaction.id, new AssetTransaction(transaction));
    setAssetTransactions(new Map(assetTransactions));
    setOpen(false);
  };

  const handleReject = async () => {};

  const validate = () => {
    const mandatoryAssetTransactionCustomFields = assetTransactionCustomFields.filter(cf => cf.mandatory);
    if (!mandatoryAssetTransactionCustomFields.length) return true;
    for (const field of mandatoryAssetTransactionCustomFields) {
      const value = input.customFields?.get(field.id)?.value;
      if (!value) return false;
    }
    return true;
  };

  const footer = disabled ? (
    <div className='flex justify-end'>
      <CancelButton onClick={() => setOpen(false)} disabled={confirmLoading || rejectLoading} />
    </div>
  ) : (
    <div className='flex justify-between'>
      <div>
        <Button onClick={handleReject} text={t(TranslationKey.reject).singular.label} />
      </div>
      <div className='flex gap-2'>
        <CancelButton onClick={() => setOpen(false)} disabled={confirmLoading || rejectLoading} />
        <Button
          onClick={handleConfirm}
          text={t(TranslationKey.confirm).singular.upper}
          loading={confirmLoading}
          disabled={!input || confirmLoading || !validate()}
        />
      </div>
    </div>
  );

  const pmd = productMasterData.get(input.product.productMasterDataId);

  return (
    <ActionModal
      title={t(TranslationKey.assetTransactionSignVerifyModalTitle).singular.upper}
      open={open}
      setOpen={setOpen}
      footer={footer}
    >
      <div className='grid grid-cols-2 gap-2'>
        <div className='col-span-1'>
          <TextInput
            label={t(TranslationKey.assetName).singular.label}
            disabled={true}
            value={pmd?.productName}
            onChange={() => {}}
          />
        </div>
        <div className='col-span-1'>
          <TextInput
            label={t(TranslationKey.assetNumber).singular.label}
            disabled={true}
            value={pmd?.productNumber}
            onChange={() => {}}
          />
        </div>
        <div className='col-span-1'>
          <TextInput
            label={t(TranslationKey.serial).singular.label}
            disabled={true}
            value={input.product.serial}
            onChange={() => {}}
          />
        </div>
        <div className='col-span-1'>
          <TextInput
            label={t(TranslationKey.quantity).singular.label}
            disabled={true}
            value={input.product.quantity.toString()}
            onChange={() => {}}
          />
        </div>

        {assetCustomFields.map(field => (
          <div className='col-span-1'>
            <CustomFieldInput
              item={field}
              value={input.product.customFields?.get(field.id)?.value}
              entity={input.product}
              onChange={() => {}}
              users={companyUsers}
              contacts={contacts}
              productMasterData={productMasterData}
              disabled={true}
            />
          </div>
        ))}
        {assetTransactionCustomFields.map(field => (
          <div className={field.type === CustomFieldType.image ? 'col-span-2' : 'col-span-1'}>
            <CustomFieldInput
              item={field}
              value={input.customFields?.get(field.id)?.value}
              entity={input}
              onChange={setInput}
              users={companyUsers}
              contacts={contacts}
              productMasterData={productMasterData}
              disabled={disabled}
            />
          </div>
        ))}
      </div>
    </ActionModal>
  );
}
